import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Briefcase, CheckSquare, TrendingUp, Bell, ArrowUpRight, Clock, Users, AlertTriangle, User, Calendar, ChevronRight, CreditCard, Check, X, Tag, CalendarDays, AlertCircle, IndianRupee, Plus, FileText, FolderPlus, Receipt, Mail, Calculator, FileSpreadsheet } from 'lucide-react';
import { API } from '@/App';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import EmailComposeDialog from '@/components/EmailComposeDialog';
import RecordTransactionDialog from '@/components/RecordTransactionDialog';
import CreateShareLinkDialog from '@/components/CreateShareLinkDialog';
// iter-360: Sponsored placements on the Dashboard.
import SponsorBadge from '@/components/SponsorBadge';
import { formatDate, formatTimestamp } from '@/utils/dateFormat';
// iter-359g: Storage/Kitchen picker dialog for "Create Estimate" quick action.
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Package, ChefHat, Share2 } from 'lucide-react';

// iter-386u-ad — Pending Amendments widget. Surfaces vendor-requested
// running-bill amendments (status='pending_amendment') the admin still
// needs to approve or reject. Renders inline on the Admin/Owner
// Dashboard so amendments never sit unnoticed.
function PendingAmendmentsWidget({ currentUserRole }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const token = localStorage.getItem('token');
  const isAdmin = ['owner', 'admin'].includes(currentUserRole || '');

  const load = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pending-amendments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(r.data?.amendments || []);
    } catch (e) {
      // silent — widget shouldn't block dashboard
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [isAdmin]); // eslint-disable-line

  const decide = async (billId, action) => {
    setProcessing(billId);
    try {
      await axios.post(
        `${API}/admin/running-bills/${billId}/${action}-amendment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(action === 'approve' ? 'Amendment approved' : 'Amendment rejected');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setProcessing(null);
    }
  };

  if (!isAdmin || rows.length === 0) return null;
  const money = (n) => `\u20B9${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="pending-amendments-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-700" />
          <h3 className="font-semibold text-amber-900 text-sm">
            Pending Amendments <span className="ml-1 text-xs opacity-70">({rows.length})</span>
          </h3>
        </div>
        <span className="text-[11px] text-amber-800">Vendor requested — awaiting your call</span>
      </div>
      <div className="space-y-2">
        {rows.slice(0, 5).map((a) => (
          <div key={a.id} className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded p-2" data-testid={`amendment-row-${a.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-stone-900 truncate">{a.title}</div>
              <div className="text-xs text-stone-500 truncate">
                {a.vendor_name || '—'} · {a.project_name || '—'}
                {a.reason ? ` · "${a.reason}"` : ''}
              </div>
            </div>
            <div className="text-xs text-right whitespace-nowrap">
              <span className="text-stone-500">{money(a.current_amount)}</span>
              <span className="text-amber-700 mx-1">→</span>
              <span className="text-amber-900 font-semibold">{money(a.pending_amount)}</span>
            </div>
            <div className="flex gap-1">
              <Button
                size="sm"
                className="h-7 bg-emerald-600 hover:bg-emerald-700 text-[11px]"
                disabled={processing === a.id}
                onClick={() => decide(a.id, 'approve')}
                data-testid={`amend-approve-${a.id}`}
              >Approve</Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-rose-700 border-rose-300 hover:bg-rose-50 text-[11px]"
                disabled={processing === a.id}
                onClick={() => decide(a.id, 'reject')}
                data-testid={`amend-reject-${a.id}`}
              >Reject</Button>
            </div>
          </div>
        ))}
        {rows.length > 5 && (
          <div className="text-[11px] text-amber-800 text-center pt-1">
            + {rows.length - 5} more — visit Vendor Running Bills to review
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [actionableItems, setActionableItems] = useState([]);
  // iter-386u-br — Active-Only Dashboard state
  const [activeProjectIds, setActiveProjectIds] = useState(new Set());
  const [projectWorksheetMap, setProjectWorksheetMap] = useState({});  // project_id → confirmed ws_id
  // iter-386u-br — Deep-link helper: BOQ row when task has an active-confirmed
  // worksheet; otherwise fall back to the Edit Task modal (never a mismatched BOQ).
  const deepLinkForTask = (task) => {
    if (!task) return '/tasks';
    // Prefer server-side link (already resolved on /dashboard/upcoming)
    if (task.link && task.link.startsWith('/boq-worksheets/')) return task.link;
    // Client-side resolution: use project's active confirmed worksheet
    const wsId = task.boq_worksheet_id || task.worksheet_id || projectWorksheetMap[task.project_id];
    if (!wsId) {
      return `/tasks?task=${task.id}&edit=1`;
    }
    const focus = task.boq_item_id || task.title || task.task_title || task.id;
    return `/boq-worksheets/${wsId}/edit?focusItem=${encodeURIComponent(focus)}`;
  };
  const [todaysTasks, setTodaysTasks] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [creditRequests, setCreditRequests] = useState([]);
  const [categoryRequests, setCategoryRequests] = useState([]);
  const [nearingDeadlineTasks, setNearingDeadlineTasks] = useState([]);
  const [upcomingItems, setUpcomingItems] = useState({ items: [], total_count: 0, type_counts: {} });
  const [fundStatus, setFundStatus] = useState({ projects: [], has_alerts: false, red_alert_count: 0 });
  // iter-359g: Storage-vs-Kitchen picker for Create Estimate quick action.
  const [estimatePickerOpen, setEstimatePickerOpen] = useState(false);
  // iter-385 — Record Transaction Quick Action
  // iter-386 — Transaction Ledger Widget
  const [recentTxns, setRecentTxns] = useState([]);
  const [txnRefreshKey, setTxnRefreshKey] = useState(0);
  // iter-386u — Show Archived Items toggle (persisted).
  const [showArchived, setShowArchived] = useState(() => {
    try { return localStorage.getItem('dashboard_show_archived') === '1'; } catch { return false; }
  });
  // Full projects list (id → archived) used to client-side filter tasks/activities.
  const [projectArchiveMap, setProjectArchiveMap] = useState({});
  // iter-386u-ar — full project map + status map for label lookup & active filter.
  const [projectMap, setProjectMap] = useState({});
  const [projectStatusMap, setProjectStatusMap] = useState({});
  // iter-386u — Create & Share Link dialog.
  const [shareLinkOpen, setShareLinkOpen] = useState(false);
  // iter-386u-d — Fund Status sort toggle: % remaining (red-alerts first) vs alphabetical.
  const [fundSort, setFundSort] = useState(() => {
    try { return localStorage.getItem('dashboard_fund_sort') || 'percent'; } catch { return 'percent'; }
  });
  useEffect(() => {
    try { localStorage.setItem('dashboard_fund_sort', fundSort); } catch (_e) { /* noop */ }
  }, [fundSort]);
  const sortedFundProjects = useMemo(() => {
    const arr = [...(fundStatus.projects || [])];
    if (fundSort === 'name') {
      arr.sort((a, b) => (a.project_name || '').localeCompare(b.project_name || ''));
    } else {
      // % remaining ascending → red (lowest) rises to the top.
      arr.sort((a, b) => (a.fund_percentage ?? 0) - (b.fund_percentage ?? 0));
    }
    return arr;
  }, [fundStatus.projects, fundSort]);
  useEffect(() => {
    try { localStorage.setItem('dashboard_show_archived', showArchived ? '1' : '0'); } catch (_e) { /* noop */ }
  }, [showArchived]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/payments/recent?limit=5`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!cancelled) setRecentTxns(r.data?.items || []);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [txnRefreshKey]);
  const [txnDialogOpen, setTxnDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionableTab, setActionableTab] = useState('all');
  const [mainTab, setMainTab] = useState(searchParams.get('tab') || 'overview');
  const [grantAmounts, setGrantAmounts] = useState({});
  const [emailComposeOpen, setEmailComposeOpen] = useState(false);
  const [emailProjectId, setEmailProjectId] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchDashboardData();
    // If URL has tab=credit-requests, switch to that tab
    if (searchParams.get('tab') === 'credit-requests') {
      setMainTab('credit-requests');
    }
    if (searchParams.get('tab') === 'category-requests') {
      setMainTab('category-requests');
    }
  }, [searchParams, showArchived]);

  const fetchDashboardData = async () => {
    try {
      const archParam = `include_archived=${showArchived ? 'true' : 'false'}`;
      const [statsRes, projectsRes, notificationsRes, tasksRes, usersRes, activitiesRes, creditReqRes, categoryReqRes, nearingDeadlineRes, fundStatusRes, upcomingRes, allProjectsRes, activeIdsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats?${archParam}`),
        axios.get(`${API}/projects?skip=0&limit=5`),
        axios.get(`${API}/notifications?unread_only=true&limit=5`),
        axios.get(`${API}/tasks`).catch(() => ({ data: [] })),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/activities`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/credit-requests`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/category-requests`).catch(() => ({ data: [] })),
        axios.get(`${API}/tasks/nearing-deadline`).catch((err) => { console.error('Failed to fetch nearing deadline tasks:', err); return { data: [] }; }),
        axios.get(`${API}/dashboard/fund-status`).catch(() => ({ data: { projects: [], has_alerts: false, red_alert_count: 0 } })),
        axios.get(`${API}/dashboard/upcoming?${archParam}`).catch(() => ({ data: { items: [], total_count: 0, type_counts: {} } })),
        // iter-386u — need archive info per project to filter tasks/activities client-side.
        axios.get(`${API}/projects?archived=all&limit=100&skip=0`).catch(() => ({ data: [] })),
        // iter-386u-br — Active-project map (with confirmed BOQ worksheet id) for filtering
        axios.get(`${API}/dashboard/active-project-ids`).catch(() => ({ data: { active_project_ids: [], project_worksheet_map: {} } })),
      ]);
      setStats(statsRes.data);
      setRecentProjects(projectsRes.data);
      setNotifications(notificationsRes.data);
      setUsers(usersRes.data);
      setCreditRequests(creditReqRes.data.filter(r => r.status === 'pending'));
      setCategoryRequests(categoryReqRes.data.filter(r => r.status === 'pending' || r.status === 'pending_approval'));
      setNearingDeadlineTasks(nearingDeadlineRes.data || []);
      setFundStatus(fundStatusRes.data || { projects: [], has_alerts: false, red_alert_count: 0 });
      setUpcomingItems(upcomingRes.data || { items: [], total_count: 0, type_counts: {} });

      // iter-386u-br — Track active-confirmed projects and their worksheet ids
      const activeIds = new Set(activeIdsRes.data?.active_project_ids || []);
      const wsMap = activeIdsRes.data?.project_worksheet_map || {};
      setActiveProjectIds(activeIds);
      setProjectWorksheetMap(wsMap);

      // iter-386u — build project → archived map for client-side filtering.
      const archMap = {};
      // iter-386u-ar — also build a project-map keyed by id so getProjectName
      // never renders "Unknown Project", plus a status-map for the active
      // filter on Calendar / Actionable widgets.
      const allProjects = allProjectsRes.data || [];
      const projMap = {};
      const statusMap = {};
      allProjects.forEach((p) => {
        archMap[p.id] = !!p.archived;
        projMap[p.id] = p;
        statusMap[p.id] = String(p.status || '').toLowerCase();
      });
      setProjectArchiveMap(archMap);
      setProjectMap(projMap);
      setProjectStatusMap(statusMap);
      // Predicate: keep item only if its project is NOT archived (unless toggle ON).
      const isProjectAllowed = (projectId) => {
        if (showArchived) return true;
        if (!projectId) return true; // orphan items always shown
        return !archMap[projectId];
      };
      
      // Initialize grant amounts
      const amounts = {};
      creditReqRes.data.forEach(r => {
        amounts[r.id] = r.credits_requested;
      });
      setGrantAmounts(amounts);
      
      // Filter actionable items (pending, not completed) — iter-386u-br
      // Active-Only: task's project must have a confirmed BOQ worksheet.
      const pendingTasks = tasksRes.data.filter(t => 
        t.status !== 'completed' && (t.status === 'not_started' || t.status === 'in_progress' || !t.approved)
        && isProjectAllowed(t.project_id)
        && (activeIds.size === 0 || activeIds.has(t.project_id))
      );
      setActionableItems(pendingTasks);
      
      // Filter today's tasks - tasks by END DATE (deadline) instead of start date
      // P0 FIX: Calendar should show tasks by deadline, not start date
      // iter-386u — apply archive filter here too.
      // iter-386u-br — Active-Only: also require confirmed BOQ worksheet.
      const today = new Date().toDateString();
      const todayTasks = tasksRes.data.filter(t => {
        if (!isProjectAllowed(t.project_id)) return false;
        if (activeIds.size > 0 && !activeIds.has(t.project_id)) return false;
        // Priority 1: Check deadline field (preferred)
        if (t.deadline) {
          const deadlineDate = new Date(t.deadline).toDateString();
          return deadlineDate === today;
        }
        // Priority 2: Check end_date field
        if (t.end_date) {
          const endDate = new Date(t.end_date).toDateString();
          return endDate === today;
        }
        // Priority 3: Check due_date field (legacy)
        if (t.due_date) {
          const dueDate = new Date(t.due_date).toDateString();
          return dueDate === today;
        }
        return false;
      }).slice(0, 5);
      setTodaysTasks(todayTasks.length > 0 ? todayTasks : pendingTasks.slice(0, 5));
      
      // Recent activities - combine tasks and activities sorted by date — iter-386u archive-aware.
      // iter-386u-br — Active-Only: task/activity's project must have a confirmed BOQ worksheet.
      const activeFilter = (item) => isProjectAllowed(item.project_id) && (activeIds.size === 0 || activeIds.has(item.project_id));
      const recentActs = [
        ...tasksRes.data.filter(activeFilter).slice(0, 10).map(t => ({
          id: t.id,
          type: 'task',
          title: t.title,
          status: t.status,
          date: t.updated_at || t.created_at,
          project_id: t.project_id,
          boq_worksheet_id: t.boq_worksheet_id,
        })),
        ...activitiesRes.data.filter(activeFilter).slice(0, 10).map(a => ({
          id: a.id,
          type: 'activity',
          title: a.name,
          status: a.status,
          date: a.updated_at || a.created_at,
          project_id: a.project_id
        }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);
      setRecentActivities(recentActs);
      
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Handle credit request approval
  const handleCreditApproval = async (requestId, approve) => {
    try {
      const credits = grantAmounts[requestId] || 1;
      await axios.post(`${API}/admin/credit-requests/approve`, {
        request_id: requestId,
        credits_to_grant: approve ? credits : 0,
        approved: approve
      });
      toast.success(approve ? `Granted ${credits} credits` : 'Request rejected');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    }
  };

  // Handle category request action
  const handleCategoryAction = async (requestId, action) => {
    try {
      await axios.post(`${API}/admin/category-requests/${requestId}/action`, {
        action: action
      });
      toast.success(action === 'approve' ? 'Category approved' : 'Category request rejected');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process category request');
    }
  };

  // Group actionable items by assigned user
  const groupByAssignee = () => {
    const grouped = {};
    actionableItems.forEach(item => {
      const assigneeId = item.assigned_to || 'unassigned';
      if (!grouped[assigneeId]) {
        grouped[assigneeId] = [];
      }
      grouped[assigneeId].push(item);
    });
    return grouped;
  };

  // Get user details
  const getUserById = (userId) => {
    if (userId === 'unassigned') return { name: 'Unassigned', role: 'none' };
    return users.find(u => u.id === userId) || { name: 'Unknown', role: 'unknown' };
  };

  // Filter users by role
  const filterByRole = (role) => {
    const grouped = groupByAssignee();
    if (role === 'all') return grouped;
    
    const filtered = {};
    Object.keys(grouped).forEach(userId => {
      const userInfo = getUserById(userId);
      if (role === 'unassigned' && userId === 'unassigned') {
        filtered[userId] = grouped[userId];
      } else if (userInfo.role === role) {
        filtered[userId] = grouped[userId];
      }
    });
    return filtered;
  };

  // Get project name — iter-386u-ar: use the full project map (from
  // /projects?archived=all) so the label never falls back to "Unknown Project".
  const getProjectName = (projectId) => {
    if (!projectId) return 'Unassigned';
    const proj = projectMap[projectId] || recentProjects.find((p) => p.id === projectId);
    return proj?.name || 'Unknown Project';
  };

  // iter-386u-ar — Active-status filter used by Calendar + Actionable widgets.
  // A project is "active" if its status is in_progress OR confirmed.
  const ACTIVE_STATUSES = new Set(['in_progress', 'confirmed', 'in progress']);
  const isProjectActive = (projectId) => {
    if (!projectId) return false;
    const s = String(projectStatusMap[projectId] || '').toLowerCase();
    // Also treat projects that don't have a fetched status yet as active so we
    // don't hide items during initial load.
    if (!s) return true;
    return ACTIVE_STATUSES.has(s);
  };

  // Format date for display
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-stone-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  const filteredGroups = filterByRole(actionableTab);
  const groupCount = Object.keys(filteredGroups).length;
  
  // Get today's date info for calendar widget
  const today = new Date();
  const dayName = today.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
  const dayNumber = today.getDate();
  const monthName = today.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();

  return (
    <Layout>
      <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="dashboard">
        {/* iter-386u-ad — Pending Amendments widget (Admin/Owner only) */}
        <PendingAmendmentsWidget currentUserRole={user?.role} />
        {/* Hero Section */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-2">
            <h1 className="font-manrope text-3xl md:text-4xl font-bold tracking-tight text-stone-900" data-testid="dashboard-title">
              Welcome back, {user.name}
            </h1>
            <p className="text-stone-500">Here&apos;s what&apos;s happening with your projects today</p>
          </div>
          {/* iter-386u — Show Archived Items toggle. When ON, stats + widgets include
              archived/inactive projects. Global financial reports & project-specific
              ledgers are unaffected. */}
          <div className="flex items-center gap-3 apple-card px-4 py-2.5 shadow-sm" data-testid="dashboard-archive-toggle-wrap">
            <div className="text-right leading-tight">
              <p className="text-xs font-semibold text-stone-900">Show Archived Items</p>
              <p className="text-[10px] text-stone-500">Include archived projects in Stats, Actionable, Calendar & Recent Updates</p>
            </div>
            <Switch
              checked={showArchived}
              onCheckedChange={setShowArchived}
              data-testid="dashboard-archive-toggle"
              aria-label="Toggle archived items"
            />
          </div>
        </div>

        {/* iter-360: Sponsored placement — clearly labelled "Sponsored".
            AI cost / rate / recommendation prompts NEVER read this data. */}
        <SponsorBadge zone="dashboard" limit={1} />

        {/* Quick Actions Widget */}
        <div className="apple-card p-4" data-testid="quick-actions-widget">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-gradient-to-br from-stone-800 to-stone-900 rounded-lg">
              <Plus size={18} className="text-stone-200" />
            </div>
            <h3 className="font-semibold text-stone-900">Quick Actions</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center gap-2 border-stone-200 hover:border-stone-400 hover:bg-stone-50 transition-all"
              onClick={() => navigate('/tasks?action=create')}
              data-testid="quick-action-create-task"
            >
              <div className="p-2 bg-stone-100 rounded-lg">
                <CheckSquare size={18} className="text-stone-700" />
              </div>
              <span className="text-xs font-medium text-stone-700">Create Task</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center gap-2 border-stone-200 hover:border-stone-400 hover:bg-stone-50 transition-all"
              onClick={() => {
                const firstProject = fundStatus.projects?.[0];
                if (firstProject) {
                  setEmailProjectId(firstProject.project_id);
                  setEmailComposeOpen(true);
                } else {
                  toast.info('No active projects found for payment request');
                }
              }}
              data-testid="quick-action-payment-request"
            >
              <div className="p-2 bg-stone-100 rounded-lg">
                <Receipt size={18} className="text-stone-700" />
              </div>
              <span className="text-xs font-medium text-stone-700">Payment Request</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center gap-2 border-stone-200 hover:border-stone-400 hover:bg-stone-50 transition-all"
              onClick={() => navigate('/documents?action=add')}
              data-testid="quick-action-add-document"
            >
              <div className="p-2 bg-stone-100 rounded-lg">
                <FileText size={18} className="text-stone-700" />
              </div>
              <span className="text-xs font-medium text-stone-700">Add Document</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center gap-2 border-stone-200 hover:border-stone-400 hover:bg-stone-50 transition-all"
              onClick={() => navigate('/projects?action=create')}
              data-testid="quick-action-create-project"
            >
              <div className="p-2 bg-green-100 rounded-lg">
                <FolderPlus size={18} className="text-green-600" />
              </div>
              <span className="text-xs font-medium text-stone-700">Create Project</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center gap-2 border-stone-200 hover:border-violet-400 hover:bg-violet-50 transition-all"
              onClick={() => navigate('/boq-worksheets?action=new')}
              data-testid="quick-action-create-boq"
            >
              <div className="p-2 bg-violet-100 rounded-lg">
                <FileSpreadsheet size={18} className="text-violet-600" />
              </div>
              <span className="text-xs font-medium text-stone-700">Create BOQ</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center gap-2 border-stone-200 hover:border-blue-400 hover:bg-blue-50 transition-all"
              onClick={() => setEstimatePickerOpen(true)}
              data-testid="quick-action-create-estimate"
            >
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calculator size={18} className="text-blue-600" />
              </div>
              <span className="text-xs font-medium text-stone-700">Create Estimate</span>
            </Button>
            {/* iter-385 — Record Transaction Quick Action */}
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center gap-2 border-stone-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all"
              onClick={() => setTxnDialogOpen(true)}
              data-testid="quick-action-record-transaction"
            >
              <div className="p-2 bg-emerald-100 rounded-lg">
                <IndianRupee size={18} className="text-emerald-600" />
              </div>
              <span className="text-xs font-medium text-stone-700">Record Transaction</span>
            </Button>
            {/* iter-386u — Create & Share Link Quick Action */}
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col items-center gap-2 border-stone-200 hover:border-amber-400 hover:bg-amber-50 transition-all"
              onClick={() => setShareLinkOpen(true)}
              data-testid="quick-action-share-link"
            >
              <div className="p-2 bg-amber-100 rounded-lg">
                <Share2 size={18} className="text-amber-600" />
              </div>
              <span className="text-xs font-medium text-stone-700">Create &amp; Share Link</span>
            </Button>
          </div>
        </div>

        {/* iter-386 — Transaction Ledger widget: last 5 transactions
            across projects, showing direction (in/out), amount, project,
            party, and mode. Refreshes when a new transaction is
            recorded via the modal. */}
        {recentTxns.length > 0 && (
          <div className="bg-white rounded-lg border border-stone-200 p-4 shadow-sm" data-testid="dashboard-recent-txns">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-stone-900">Recent Transactions</h3>
                <p className="text-[11px] text-stone-500">Last 5 across all projects — click a row to open the finance ledger.</p>
              </div>
              <button
                type="button"
                onClick={() => setTxnRefreshKey((k) => k + 1)}
                className="text-[10px] text-stone-500 hover:text-stone-700"
                data-testid="dashboard-recent-txns-refresh"
              >Refresh</button>
            </div>
            <div className="divide-y divide-stone-100">
              {recentTxns.map((t, i) => (
                <button
                  key={t.id || i}
                  type="button"
                  onClick={() => t.project_id && navigate(`/project-accounts?projectId=${t.project_id}&tab=transactions`)}
                  className="w-full flex items-center justify-between gap-3 py-2 hover:bg-stone-50 text-left"
                  data-testid={`recent-txn-row-${i}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {t.direction === 'client_received' ? (
                      <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px] font-bold" title="Client Received">IN</span>
                    ) : (
                      <span className="shrink-0 w-6 h-6 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-[10px] font-bold" title="Vendor Paid">OUT</span>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm text-stone-900 truncate">
                        <span className="font-semibold">{t.party_name || '—'}</span>
                        <span className="text-stone-400"> · </span>
                        <span className="text-stone-600">{t.project_name}</span>
                      </div>
                      <div className="text-[10px] text-stone-500 truncate">
                        {t.payment_mode} {t.reference ? `· ${t.reference}` : ''} {t.notes ? `· ${t.notes.slice(0, 60)}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className={`text-sm font-bold whitespace-nowrap ${t.direction === 'client_received' ? 'text-emerald-700' : 'text-red-700'}`}>
                    {t.direction === 'client_received' ? '+' : '-'}₹{Number(t.amount || 0).toLocaleString('en-IN')}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* iter-386u-c — PROJECT FUND STATUS WIDGET moved here (directly below Recent Transactions) */}
        {fundStatus.projects.length > 0 && (
          <div className="apple-card p-4" data-testid="fund-status-widget">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className={`p-2 rounded-lg ${fundStatus.has_alerts ? 'bg-red-100' : 'bg-green-100'}`}>
                  <TrendingUp size={20} className={fundStatus.has_alerts ? 'text-red-600' : 'text-green-600'} />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900">Project Fund Status</h3>
                  <p className="text-[11px] text-stone-500">
                    All projects marked <span className="font-semibold">In Progress</span> — auto-added when a project moves here.
                  </p>
                  {fundStatus.has_alerts && (
                    <p className="text-xs text-red-600 font-medium">
                      {fundStatus.red_alert_count} project{fundStatus.red_alert_count > 1 ? 's' : ''} need attention
                    </p>
                  )}
                </div>
              </div>
              {fundStatus.has_alerts && (
                <Badge variant="destructive" className="bg-red-600 animate-pulse">
                  <AlertCircle size={14} className="mr-1" /> LOW FUND
                </Badge>
              )}
            </div>

            {/* iter-386u-d — Sort toggle */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Sort:</span>
              <button
                type="button"
                onClick={() => setFundSort('percent')}
                className={`text-[11px] px-2 py-1 rounded font-medium ${fundSort === 'percent' ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                data-testid="fund-sort-percent"
                title="Red-alert projects rise to the top"
              >
                % Remaining
              </button>
              <button
                type="button"
                onClick={() => setFundSort('name')}
                className={`text-[11px] px-2 py-1 rounded font-medium ${fundSort === 'name' ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
                data-testid="fund-sort-name"
              >
                Alphabetical
              </button>
            </div>

            <div className="space-y-3">
              {sortedFundProjects.map((project) => (
                <div
                  key={project.project_id}
                  onClick={() => navigate(`/projects/${project.project_id}/dashboard`)}
                  className="flex items-center justify-between p-3 rounded-lg bg-stone-50 hover:bg-stone-100 cursor-pointer transition-colors"
                  data-testid={`fund-status-row-${project.project_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-900 truncate">{project.project_name}</p>
                    <p className="text-xs text-stone-500">
                      Contract: ₹{(project.contract_value / 100000).toFixed(2)}L
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm font-semibold text-stone-900">
                        ₹{(project.available_fund / 100000).toFixed(2)}L
                      </p>
                      <p className="text-xs text-stone-500">available</p>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      project.status === 'green' ? 'bg-green-100 text-green-700' :
                      project.status === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {project.fund_percentage.toFixed(0)}%
                    </div>
                    {!project.token_received && (
                      <Badge variant="outline" className="border-orange-400 text-orange-600 text-xs">
                        Token Pending
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {fundStatus.projects.some(p => p.status === 'red') && (
              <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-800 font-medium flex items-center gap-2">
                  <AlertCircle size={16} />
                  Project fund below 10%. Raise next client payment request.
                </p>
                <Button
                  size="sm"
                  className="mt-2 bg-red-600 hover:bg-red-700 gap-1.5"
                  onClick={() => {
                    const redProject = fundStatus.projects.find(p => p.status === 'red');
                    if (redProject) {
                      setEmailProjectId(redProject.project_id);
                      setEmailComposeOpen(true);
                    }
                  }}
                  data-testid="dashboard-generate-payment-request"
                >
                  <Mail size={14} /> Generate Payment Request
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Stats Grid - Responsive */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Total Projects */}
          <div
            onClick={() => navigate('/projects')}
            className="apple-card p-4 cursor-pointer group hover:scale-[1.02] transition-transform"
            data-testid="stat-total-projects"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-stone-100 rounded-xl">
                <Briefcase size={20} className="text-stone-700" />
              </div>
              <ArrowUpRight size={16} className="text-stone-400 group-hover:text-stone-700 ml-auto" />
            </div>
            <p className="text-xs text-stone-500 uppercase tracking-wider">Projects</p>
            <p className="font-manrope text-3xl font-bold text-stone-900">{stats?.total_projects || 0}</p>
            <p className="text-xs text-stone-400 mt-1">{stats?.in_progress_projects || 0} active</p>
          </div>

          {/* Tasks Card */}
          <div
            onClick={() => navigate('/tasks')}
            className="apple-card p-4 cursor-pointer group hover:scale-[1.02] transition-transform"
            data-testid="stat-tasks"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-purple-100 rounded-xl">
                <CheckSquare size={20} className="text-purple-600" />
              </div>
              <ArrowUpRight size={16} className="text-stone-400 group-hover:text-purple-600 ml-auto" />
            </div>
            <p className="text-xs text-stone-500 uppercase tracking-wider">Tasks</p>
            <p className="font-manrope text-3xl font-bold text-stone-900">{stats?.total_tasks || 0}</p>
            <p className="text-xs text-stone-400 mt-1">{actionableItems.length} pending</p>
          </div>

          {/* Financial Overview */}
          <div
            onClick={() => navigate('/invoices')}
            className="apple-card p-4 cursor-pointer group hover:scale-[1.02] transition-transform"
            data-testid="stat-financials"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-green-100 rounded-xl">
                <TrendingUp size={20} className="text-green-600" />
              </div>
              <ArrowUpRight size={16} className="text-stone-400 group-hover:text-green-600 ml-auto" />
            </div>
            <p className="text-xs text-stone-500 uppercase tracking-wider">Invoiced</p>
            <p className="font-manrope text-2xl font-bold text-stone-900">
              ₹{((stats?.total_invoiced || 0) / 100000).toFixed(1)}L
            </p>
            <p className="text-xs text-green-600 mt-1">₹{((stats?.total_collected || 0) / 100000).toFixed(1)}L collected</p>
          </div>

          {/* Pending Card */}
          <div
            onClick={() => navigate('/tasks')}
            className="apple-card p-4 cursor-pointer group hover:scale-[1.02] transition-transform"
            data-testid="stat-pending"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-stone-100 rounded-xl">
                <AlertTriangle size={20} className="text-stone-600" />
              </div>
              <ArrowUpRight size={16} className="text-stone-400 group-hover:text-stone-600 ml-auto" />
            </div>
            <p className="text-xs text-stone-500 uppercase tracking-wider">Pending</p>
            <p className="font-manrope text-3xl font-bold text-stone-900">{actionableItems.length}</p>
            <p className="text-xs text-stone-600 mt-1">Need attention</p>
          </div>
        </div>

        {/* iter-386u-c — Fund Status widget moved above (below Recent Transactions). */}

        {/* Credit Requests Alert Banner - Show only if pending requests exist */}
        {creditRequests.length > 0 && (
          <div 
            className="apple-card p-4 bg-gradient-to-r from-stone-50 to-stone-100 border-stone-200 cursor-pointer"
            onClick={() => setMainTab('credit-requests')}
            data-testid="credit-requests-banner"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-stone-200 rounded-full">
                  <CreditCard size={20} className="text-stone-700" />
                </div>
                <div>
                  <p className="font-semibold text-stone-900">
                    {creditRequests.length} AI Credit Request{creditRequests.length > 1 ? 's' : ''} Pending
                  </p>
                  <p className="text-sm text-stone-600">Click to review and approve</p>
                </div>
              </div>
              <Badge variant="secondary" className="bg-stone-700 text-white">{creditRequests.length}</Badge>
            </div>
          </div>
        )}

        {/* Category Requests Alert Banner - Show only if pending requests exist */}
        {categoryRequests.length > 0 && (
          <div 
            className="apple-card p-4 bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200 cursor-pointer"
            onClick={() => setMainTab('category-requests')}
            data-testid="category-requests-banner"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-200 rounded-full">
                  <Tag size={20} className="text-purple-700" />
                </div>
                <div>
                  <p className="font-semibold text-purple-900">
                    {categoryRequests.length} New Rate Category Request{categoryRequests.length > 1 ? 's' : ''} Pending
                  </p>
                  {categoryRequests[0] && (
                    <p className="text-sm text-purple-700">
                      <span className="font-medium">{categoryRequests[0].vendor_name || 'Vendor'}</span> requests: {categoryRequests[0].category_name}
                    </p>
                  )}
                </div>
              </div>
              <Badge variant="destructive" className="bg-purple-600">{categoryRequests.length}</Badge>
            </div>
          </div>
        )}

        {/* Main Tabs - Overview vs Credit Requests vs Category Requests */}
        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
          <TabsList className="grid w-full max-w-xl grid-cols-3">
            <TabsTrigger value="overview">Dashboard Overview</TabsTrigger>
            <TabsTrigger value="credit-requests" className="relative">
              Credit Requests
              {creditRequests.length > 0 && (
                <Badge variant="destructive" className="ml-2 px-1.5 py-0 text-xs">{creditRequests.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="category-requests" className="relative">
              Category Requests
              {categoryRequests.length > 0 && (
                <Badge variant="destructive" className="ml-2 px-1.5 py-0 text-xs">{categoryRequests.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="credit-requests" className="mt-4">
            <div className="apple-card p-6">
              <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <CreditCard size={20} />
                Pending AI Credit Requests
              </h3>
              
              {creditRequests.length === 0 ? (
                <div className="text-center py-8">
                  <Check size={48} className="mx-auto text-green-400 mb-3" />
                  <p className="text-stone-500">All credit requests have been processed!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {creditRequests.map((request) => (
                    <div key={request.id} className="border border-stone-200 rounded-lg p-4 bg-stone-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-stone-900">{request.user_name}</p>
                          <p className="text-sm text-stone-500">{request.user_email}</p>
                          <p className="text-sm text-stone-600 mt-2">
                            <span className="font-medium">Requested:</span> {request.credits_requested} credits
                          </p>
                          <p className="text-sm text-stone-600">
                            <span className="font-medium">Reason:</span> {request.reason}
                          </p>
                          <p className="text-xs text-stone-400 mt-1">
                            {new Date(request.created_at).toLocaleString()}
                          </p>
                        </div>
                        
                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-stone-600">Grant:</span>
                            <Input
                              type="number"
                              min="1"
                              max={request.credits_requested}
                              value={grantAmounts[request.id] || request.credits_requested}
                              onChange={(e) => setGrantAmounts({
                                ...grantAmounts,
                                [request.id]: Math.min(Math.max(1, parseInt(e.target.value) || 1), request.credits_requested)
                              })}
                              className="w-16 text-center"
                              data-testid={`grant-amount-${request.id}`}
                            />
                            <span className="text-sm text-stone-400">/ {request.credits_requested}</span>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleCreditApproval(request.id, true)}
                              data-testid={`approve-credit-${request.id}`}
                            >
                              <Check size={14} className="mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleCreditApproval(request.id, false)}
                              data-testid={`reject-credit-${request.id}`}
                            >
                              <X size={14} className="mr-1" /> Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="category-requests" className="mt-4">
            <div className="apple-card p-6">
              <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <Tag size={20} className="text-purple-600" />
                Pending Rate Category Requests
              </h3>
              
              {categoryRequests.length === 0 ? (
                <div className="text-center py-8">
                  <Check size={48} className="mx-auto text-green-400 mb-3" />
                  <p className="text-stone-500">All category requests have been processed!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {categoryRequests.map((request) => (
                    <div key={request.id} className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Vendor Info Header */}
                          <div className="bg-white rounded-lg p-3 mb-3 border border-purple-100">
                            <p className="font-semibold text-stone-900">
                              {request.vendor_name || 'Vendor'}
                            </p>
                            {request.vendor_company && (
                              <p className="text-sm text-stone-500">{request.vendor_company}</p>
                            )}
                            {request.existing_categories && request.existing_categories.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs text-stone-400 mb-1">Current Approved Categories:</p>
                                <div className="flex flex-wrap gap-1">
                                  {request.existing_categories.slice(0, 3).map((cat, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                      {cat}
                                    </Badge>
                                  ))}
                                  {request.existing_categories.length > 3 && (
                                    <Badge variant="outline" className="text-xs">
                                      +{request.existing_categories.length - 3} more
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Requested Category */}
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
                              {request.category_group || 'General'}
                            </Badge>
                            <span className="text-xs text-stone-400">Requested Category</span>
                          </div>
                          <p className="font-semibold text-lg text-purple-900">{request.category_name}</p>
                          
                          {request.suggested_unit && (
                            <p className="text-sm text-stone-600 mt-1">
                              <span className="font-medium">Suggested Unit:</span> {request.suggested_unit}
                            </p>
                          )}
                          {request.description && (
                            <p className="text-sm text-stone-600 mt-1">
                              <span className="font-medium">Description:</span> {request.description}
                            </p>
                          )}
                          {request.reason && (
                            <p className="text-sm text-stone-600 mt-1">
                              <span className="font-medium">Reason:</span> {request.reason}
                            </p>
                          )}
                          <p className="text-xs text-stone-400 mt-2">
                            {new Date(request.created_at).toLocaleString()}
                          </p>
                        </div>
                        
                        <div className="flex flex-col gap-2 ml-4">
                          <Button
                            size="sm"
                            className="bg-purple-600 hover:bg-purple-700"
                            onClick={() => handleCategoryAction(request.id, 'approve')}
                            data-testid={`approve-category-${request.id}`}
                          >
                            <Check size={14} className="mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-stone-600 border-stone-200 hover:bg-stone-50"
                            onClick={() => {
                              const newName = prompt('Enter new category name:', request.category_name);
                              if (newName && newName !== request.category_name) {
                                // TODO: Implement rename & approve
                                toast.info('Rename feature coming soon');
                              }
                            }}
                            data-testid={`rename-category-${request.id}`}
                          >
                            ✏️ Rename & Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleCategoryAction(request.id, 'reject')}
                            data-testid={`reject-category-${request.id}`}
                          >
                            <X size={14} className="mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="overview" className="mt-4">
            {/* UNIFIED: Upcoming (Next 7 Days) - Tasks, Appointments, Reminders, Payment Due */}
            {upcomingItems.items.length > 0 && (() => {
              // iter-386u-ar — filter upcoming items to only show those tied to
              // ACTIVE projects (in_progress / confirmed). Items with no
              // project_id (personal reminders) always pass through.
              const activeItems = (upcomingItems.items || []).filter((it) => {
                const pid = it.project_id || it.project?.id;
                if (!pid) return true;
                return isProjectActive(pid);
              });
              const activeTypeCounts = { task: 0, appointment: 0, reminder: 0, payment_due: 0 };
              for (const it of activeItems) activeTypeCounts[it.type] = (activeTypeCounts[it.type] || 0) + 1;
              return activeItems.length === 0 ? null : (
              <div className="apple-card p-6 mb-4 border-l-4 border-amber-500" data-testid="upcoming-widget">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 rounded-full">
                      <CalendarDays size={22} className="text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-stone-900">Upcoming (Next 7 Days)</h3>
                      <p className="text-sm text-stone-500">
                        {activeItems.length} item{activeItems.length !== 1 ? 's' : ''} need attention
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {activeTypeCounts.task > 0 && (
                      <Badge className="bg-blue-100 text-blue-700 border-blue-200 px-2 text-xs">
                        {activeTypeCounts.task} Tasks
                      </Badge>
                    )}
                    {activeTypeCounts.appointment > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200 px-2 text-xs">
                        {activeTypeCounts.appointment} Appointments
                      </Badge>
                    )}
                    {activeTypeCounts.reminder > 0 && (
                      <Badge className="bg-purple-100 text-purple-700 border-purple-200 px-2 text-xs">
                        {activeTypeCounts.reminder} Reminders
                      </Badge>
                    )}
                    {activeTypeCounts.payment_due > 0 && (
                      <Badge className="bg-red-100 text-red-700 border-red-200 px-2 text-xs animate-pulse">
                        {activeTypeCounts.payment_due} Payments Due
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2 max-h-[350px] overflow-y-auto">
                  {activeItems.slice(0, 15).map((item) => {
                    // Determine colors based on type
                    const typeStyles = {
                      task: { bg: 'bg-blue-50 hover:bg-blue-100', border: 'border-blue-500', icon: <CheckSquare size={16} className="text-blue-600" />, textColor: 'text-blue-600' },
                      appointment: { bg: 'bg-amber-50 hover:bg-amber-100', border: 'border-amber-500', icon: <Calendar size={16} className="text-amber-600" />, textColor: 'text-amber-600' },
                      reminder: { bg: 'bg-purple-50 hover:bg-purple-100', border: 'border-purple-500', icon: <Bell size={16} className="text-purple-600" />, textColor: 'text-purple-600' },
                      payment_due: { bg: 'bg-red-50 hover:bg-red-100', border: 'border-red-500', icon: <IndianRupee size={16} className="text-red-600" />, textColor: 'text-red-600' },
                    };
                    const style = typeStyles[item.type] || typeStyles.task;
                    
                    return (
                      <div 
                        key={item.id}
                        className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-colors ${style.bg} border-l-4 ${style.border}`}
                        onClick={() => navigate(item.link)}
                        data-testid={`upcoming-item-${item.id}`}
                      >
                        {/* Type Icon */}
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
                          {style.icon}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="font-medium text-stone-900 truncate">{item.title}</p>
                            <Badge variant="outline" className={`text-xs capitalize ${style.textColor} bg-white`}>
                              {item.type === 'payment_due' ? 'Payment' : item.type}
                            </Badge>
                          </div>
                          <p className="text-sm text-stone-500 truncate">
                            {item.project_name !== '—' ? item.project_name : 'No Project'}
                          </p>
                        </div>
                        
                        {/* Date/Time */}
                        <div className="flex items-center gap-2 flex-shrink-0 text-right">
                          <div>
                            <p className={`text-sm font-medium ${style.textColor}`}>
                              {item.date_display === 'Due Now' ? item.date_display : 
                               formatDate(new Date(item.date), { shortMonth: true })}
                            </p>
                            {item.time && (
                              <p className="text-xs text-stone-400">{item.time}</p>
                            )}
                          </div>
                          
                          {item.priority === 'critical' && (
                            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {upcomingItems.total_count > 15 && (
                  <button 
                    className="mt-3 text-sm text-amber-600 hover:text-amber-700 font-medium"
                    onClick={() => navigate('/calendar')}
                  >
                    View all {upcomingItems.total_count} items →
                  </button>
                )}
              </div>
              );
            })()}

            {/* Second Row - Today's Tasks Widget and Recent Updates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Today's Tasks Widget - iPhone Calendar Style */}
          <div 
            className="apple-card overflow-hidden"
            data-testid="todays-tasks-widget"
          >
            <div 
              className="bg-gradient-to-r from-red-500 to-red-600 p-4 text-white cursor-pointer hover:from-red-600 hover:to-red-700 transition-colors"
              onClick={() => navigate('/calendar')}
              title="Open Full Calendar"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold tracking-wider opacity-90">{dayName}</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold">{dayNumber}</span>
                    <span className="text-lg font-medium opacity-80">{monthName}</span>
                  </div>
                </div>
                <div className="text-white/80 text-xs flex items-center gap-1">
                  <CalendarDays size={16} />
                  <span>Full Calendar →</span>
                </div>
              </div>
            </div>
            <div className="p-4 space-y-3 max-h-[200px] overflow-y-auto">
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Today&apos;s Tasks (By Deadline)</p>
              {todaysTasks.length === 0 ? (
                <p className="text-sm text-stone-400 py-4 text-center">No tasks due today</p>
              ) : (
                todaysTasks.map((task, idx) => (
                  <div 
                    key={task.id} 
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50 transition-colors cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); navigate(deepLinkForTask(task)); }}
                  >
                    <div className={`w-1 h-8 rounded-full ${
                      task.status === 'completed' ? 'bg-green-500' :
                      task.status === 'in_progress' ? 'bg-blue-500' :
                      'bg-amber-500'
                    }`}></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-900 truncate">{task.title}</p>
                      <p className="text-xs text-stone-500 truncate">{getProjectName(task.project_id)}</p>
                    </div>
                    <ChevronRight size={16} className="text-stone-400" />
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Updates Card */}
          <div className="apple-card p-4" data-testid="notifications-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-stone-100 rounded-xl">
                  <Bell size={20} className="text-stone-700" />
                </div>
                <h3 className="font-semibold text-stone-900">Recent Updates</h3>
              </div>
              <button
                onClick={() => navigate('/schedule')}
                className="text-xs font-medium text-stone-500 hover:text-stone-700"
              >
                View All
              </button>
            </div>
            <div className="space-y-3 max-h-[200px] overflow-y-auto">
              {recentActivities.length === 0 ? (
                <p className="text-sm text-stone-400 py-4 text-center">No recent activity</p>
              ) : (
                recentActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex gap-3 p-2 rounded-lg hover:bg-stone-50 transition-colors cursor-pointer"
                    onClick={() => navigate(activity.type === 'task' ? deepLinkForTask(activity) : '/schedule')}
                  >
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                      activity.status === 'completed' ? 'bg-green-500' :
                      activity.status === 'in_progress' ? 'bg-blue-500' :
                      'bg-stone-400'
                    }`}></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-900 truncate">{activity.title}</p>
                      <div className="flex items-center gap-2 text-xs text-stone-500">
                        <span className="capitalize">{activity.type}</span>
                        <span>•</span>
                        <span>{formatTimestamp(activity.date)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Actionable Items by Person */}
        <div className="apple-card p-4 md:p-6" data-testid="actionable-items">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div>
              <h2 className="font-manrope text-xl font-bold text-stone-900">Actionable Items</h2>
              <p className="text-sm text-stone-500">Tasks pending action, grouped by assignee</p>
            </div>
            <div className="flex items-center gap-2 bg-amber-50 px-3 py-2 rounded-lg">
              <AlertTriangle size={18} className="text-amber-500" />
              <span className="font-bold text-stone-900">{actionableItems.length}</span>
              <span className="text-sm text-stone-500">pending</span>
            </div>
          </div>

          {/* Filter Tabs */}
          <Tabs value={actionableTab} onValueChange={setActionableTab} className="w-full">
            <TabsList className="flex flex-wrap gap-1 mb-4 bg-stone-100 p-1 rounded-lg">
              <TabsTrigger value="all" className="text-xs md:text-sm text-stone-700 data-[state=active]:bg-white px-3 py-1.5">
                All
              </TabsTrigger>
              <TabsTrigger value="owner" className="text-xs md:text-sm text-stone-700 data-[state=active]:bg-white px-3 py-1.5">
                Staff
              </TabsTrigger>
              <TabsTrigger value="vendor" className="text-xs md:text-sm text-stone-700 data-[state=active]:bg-white px-3 py-1.5">
                Vendor
              </TabsTrigger>
              <TabsTrigger value="labour" className="text-xs md:text-sm text-stone-700 data-[state=active]:bg-white px-3 py-1.5">
                Labour
              </TabsTrigger>
              <TabsTrigger value="client" className="text-xs md:text-sm text-stone-700 data-[state=active]:bg-white px-3 py-1.5">
                Client
              </TabsTrigger>
            </TabsList>

            <TabsContent value={actionableTab} className="mt-0">
              {groupCount === 0 ? (
                <div className="text-center py-8 text-stone-400">
                  <CheckSquare size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No pending items for this category</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                  {Object.entries(filteredGroups).map(([userId, tasks]) => {
                    const userInfo = getUserById(userId);
                    const roleColors = {
                      owner: 'bg-stone-800 text-white',
                      staff: 'bg-blue-500 text-white',
                      vendor: 'bg-amber-500 text-white',
                      labour: 'bg-orange-500 text-white',
                      client: 'bg-green-500 text-white',
                      none: 'bg-stone-400 text-white',
                    };
                    
                    return (
                      <div key={userId} className="border border-stone-200 rounded-xl overflow-hidden">
                        {/* Person Header */}
                        <div className="bg-stone-50 px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${roleColors[userInfo.role] || roleColors.none}`}>
                              <User size={14} />
                            </div>
                            <div>
                              <p className="font-medium text-stone-900 text-sm">{userInfo.name}</p>
                              <p className="text-xs text-stone-500 capitalize">{userInfo.role}</p>
                            </div>
                          </div>
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                            {tasks.length} pending
                          </span>
                        </div>
                        
                        {/* Tasks List */}
                        <div className="divide-y divide-stone-100">
                          {tasks.slice(0, 5).map((task) => (
                            <div
                              key={task.id}
                              onClick={() => navigate(deepLinkForTask(task))}
                              className="px-4 py-3 hover:bg-stone-50 cursor-pointer transition-colors"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-stone-900 text-sm truncate">{task.title}</p>
                                  <p className="text-xs text-stone-500 truncate">{getProjectName(task.project_id)}</p>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {!task.approved && (
                                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs whitespace-nowrap">
                                      Approval
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {tasks.length > 5 && (
                            <div className="px-4 py-2 text-center">
                              <button
                                onClick={() => navigate('/tasks')}
                                className="text-xs text-stone-500 hover:text-stone-700"
                              >
                                +{tasks.length - 5} more tasks
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
          </TabsContent>
        </Tabs>
      </div>

      <EmailComposeDialog
        open={emailComposeOpen}
        onOpenChange={setEmailComposeOpen}
        projectId={emailProjectId}
        templateType="payment_request"
      />

      {/* iter-359g: Storage-vs-Kitchen picker for the Create Estimate quick action. */}
      <Dialog open={estimatePickerOpen} onOpenChange={setEstimatePickerOpen}>
        <DialogContent className="max-w-md" data-testid="create-estimate-picker">
          <DialogHeader>
            <DialogTitle className="text-stone-900 flex items-center gap-2">
              <Calculator size={16} className="text-blue-600" />
              Which estimator?
            </DialogTitle>
            <DialogDescription className="text-stone-500 text-sm">
              Pick the estimator that fits the scope you want to cost — you can switch later.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              type="button"
              onClick={() => { setEstimatePickerOpen(false); navigate('/estimators/wardrobe'); }}
              data-testid="create-estimate-storage"
              className="flex flex-col items-center justify-center gap-3 p-4 border-2 border-stone-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
            >
              <div className="p-3 bg-blue-100 rounded-2xl group-hover:scale-110 transition-transform">
                <Package size={28} className="text-blue-700" />
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold text-stone-900">Storage Estimator</div>
                <div className="text-[11px] text-stone-500 mt-0.5">Wardrobes, TV units, custom joinery</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => { setEstimatePickerOpen(false); navigate('/estimators/kitchen'); }}
              data-testid="create-estimate-kitchen"
              className="flex flex-col items-center justify-center gap-3 p-4 border-2 border-stone-200 rounded-xl hover:border-amber-500 hover:bg-amber-50 transition-all group"
            >
              <div className="p-3 bg-amber-100 rounded-2xl group-hover:scale-110 transition-transform">
                <ChefHat size={28} className="text-amber-700" />
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold text-stone-900">Kitchen Estimator</div>
                <div className="text-[11px] text-stone-500 mt-0.5">Modular kitchen, counter, appliances</div>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* iter-385 — Record Transaction Quick Action modal */}
      <RecordTransactionDialog
        open={txnDialogOpen}
        onOpenChange={setTxnDialogOpen}
        onRecorded={() => {
          // iter-386 — bump recent-txns fetch so the ledger widget updates.
          setTxnRefreshKey((k) => k + 1);
        }}
      />
      {/* iter-386u — Create & Share Link modal (Vendor Quote Link OR Client Project Status Link) */}
      <CreateShareLinkDialog
        open={shareLinkOpen}
        onOpenChange={setShareLinkOpen}
      />
    </Layout>
  );
}
